import { useState } from "react";
import { Mail, LifeBuoy, Send } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DisclaimerBanner } from "../components/Layout";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const SUPPORT_EMAIL = "WateredSouls@MysticBr8w.com";

export default function Support() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!category) { toast.error("Please choose a category."); return; }
    setSending(true);
    try {
      await api.post("/support/ticket", { name, email, category, message });
      toast.success("Your message has been received. We'll follow up by email.");
      setMessage("");
      setCategory("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center">
        <span className="w-14 h-14 mx-auto rounded-full bg-[#2C4A3E] flex items-center justify-center">
          <LifeBuoy className="w-7 h-7 text-[#E8ECE8]" />
        </span>
        <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-[#1A2621]">Contact Support</h1>
        <p className="mt-2 text-[#5A6B61]">Having trouble with login, payment, or the course? We're here to help.</p>
      </div>

      <div className="mt-8 bg-[#EBE5DC] border border-[#2C4A3E]/15 rounded-2xl p-5 flex items-center gap-3">
        <Mail className="w-5 h-5 text-[#C47A53]" />
        <div>
          <p className="text-sm text-[#5A6B61]">Email us directly at</p>
          <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-email" className="font-mono text-[#2C4A3E] font-medium">{SUPPORT_EMAIL}</a>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl p-6 space-y-4">
        <h2 className="font-serif text-xl text-[#1A2621]">Submit a technical issue</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#2C4A3E] mb-1">Name</label>
            <input data-testid="support-name" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-[#2C4A3E]/20 bg-white/70 focus:outline-none focus:border-[#2C4A3E]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2C4A3E] mb-1">Email</label>
            <input data-testid="support-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-[#2C4A3E]/20 bg-white/70 focus:outline-none focus:border-[#2C4A3E]" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2C4A3E] mb-1">Category</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="support-category" className="bg-white/70 border-[#2C4A3E]/20 rounded-xl py-3 h-auto">
              <SelectValue placeholder="Choose a topic" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="login">Login trouble</SelectItem>
              <SelectItem value="payment">Payment / enrollment</SelectItem>
              <SelectItem value="course">Course content</SelectItem>
              <SelectItem value="certificate">Certificate</SelectItem>
              <SelectItem value="other">Something else</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2C4A3E] mb-1">Message</label>
          <textarea data-testid="support-message" required rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-[#2C4A3E]/20 bg-white/70 focus:outline-none focus:border-[#2C4A3E] resize-none" />
        </div>
        <button data-testid="support-submit" type="submit" disabled={sending}
          className="w-full py-3 rounded-xl bg-[#2C4A3E] text-[#FAF8F5] font-semibold hover:bg-[#223B31] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send message"}
        </button>
      </form>

      <div className="mt-6"><DisclaimerBanner /></div>
    </div>
  );
}
